/**
 * PrepPodSuccess.jsx — /pod/success
 *
 * Landing page after Stripe Checkout. Reads ?session_id, calls
 * /api/prep-pod/checkout/verify to confirm payment, then shows the
 * branded confirmation ("Margaret will text you the schedule within 24 hours").
 * Idempotent — safe to reload or bookmark.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Heart, Loader2, MapPin, MessageSquare, XCircle } from 'lucide-react';
import api from '../services/api';

const C = {
  ink: '#0A0A0F',
  card: '#14141B',
  volt: '#FFC72C',
  mint: '#A8E6CF',
  subtle: '#27272A',
  dim: '#A1A1AA',
};

const LOCATION_ADDR = 'Handlelife HQ · 20061 Prairie St, Chatsworth, CA';

export default function PrepPodSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState({ loading: true, paid: false, data: null, error: '' });

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, data: null, error: 'Missing session_id in URL.' });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/prep-pod/checkout/verify?session_id=${encodeURIComponent(sessionId)}`);
        setState({ loading: false, paid: !!r.data.paid, data: r.data, error: '' });
      } catch (e) {
        setState({
          loading: false,
          paid: false,
          data: null,
          error: e?.response?.data?.detail || 'We could not confirm your payment yet.',
        });
      }
    })();
  }, [sessionId]);

  return (
    <div className="min-h-screen" style={{ background: C.ink, color: '#fff', fontFamily: 'Montserrat, sans-serif' }} data-testid="prep-pod-success-page">
      <section className="py-20 md:py-32">
        <div className="container mx-auto max-w-2xl px-6">
          <div className="text-[10px] font-black tracking-[0.32em] uppercase mb-3" style={{ color: C.volt }}>
            The Prep Pod
          </div>

          {state.loading && (
            <div className="flex items-center gap-3 py-16" data-testid="prep-pod-success-loading">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.volt }} />
              <div className="text-lg">Confirming your payment with Stripe…</div>
            </div>
          )}

          {!state.loading && state.paid && state.data?.kind === 'tip' && (
            <div data-testid="prep-pod-success-tip">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ background: '#FFD3B6' }}>
                <Heart className="w-8 h-8" style={{ color: C.ink }} strokeWidth={2.5} fill={C.ink} />
              </div>
              <h1 className="uppercase leading-[0.95] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>
                Thank <span style={{ color: '#FFD3B6' }}>You.</span>
              </h1>
              <p className="text-base md:text-lg mb-8" style={{ color: C.dim, lineHeight: 1.6 }}>
                Your gift of <strong style={{ color: '#fff' }}>${((state.data?.amount_cents || 0) / 100).toFixed(2)}</strong> just funded supplies, coaches, and scholarship seats for kids who need The Prep Pod. Every dollar shows up on the ground — we see you.
              </p>
              <div className="p-5 border-l-2 mb-8" style={{ borderColor: '#FFD3B6', background: C.card }}>
                <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-1" style={{ color: '#FFD3B6' }}>What Happens Next</div>
                <div className="text-sm" style={{ color: '#fff' }}>
                  Margaret will send a personal thank-you note to
                  {' '}<strong>{state.data?.tipper_email || 'you'}</strong> within 24 hours.
                </div>
              </div>
              <div className="mt-10">
                <Link
                  to="/prep-pod"
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em] border-2"
                  style={{ borderColor: C.subtle, color: '#fff' }}
                  data-testid="prep-pod-success-back-tip"
                >
                  Back to The Prep Pod
                </Link>
              </div>
            </div>
          )}

          {!state.loading && state.paid && state.data?.kind !== 'tip' && (
            <div data-testid="prep-pod-success-paid">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ background: C.mint }}>
                <Check className="w-8 h-8" style={{ color: C.ink }} strokeWidth={3} />
              </div>
              <h1 className="uppercase leading-[0.95] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>
                You're <span style={{ color: C.volt }}>In.</span>
              </h1>
              <p className="text-base md:text-lg mb-8" style={{ color: C.dim, lineHeight: 1.6 }}>
                Payment confirmed for <strong style={{ color: '#fff' }}>{state.data?.tier_label}</strong>
                {state.data?.sessions_purchased ? (
                  <> — <strong style={{ color: '#fff' }}>{state.data.sessions_purchased} session{state.data.sessions_purchased !== 1 ? 's' : ''}</strong> unlocked.</>
                ) : '.'}
              </p>

              <div className="p-5 md:p-6 border-2 mb-6" style={{ background: C.card, borderColor: C.volt }}>
                <div className="flex items-start gap-4">
                  <MessageSquare className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: C.volt }} />
                  <div>
                    <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-1" style={{ color: C.volt }}>Next Step</div>
                    <div className="text-lg md:text-xl font-black uppercase tracking-tight mb-2">
                      Margaret will text you within 24 hours.
                    </div>
                    <div className="text-sm" style={{ color: C.dim }}>
                      She'll lock in your first day at Handlelife HQ and share exact drop-off times, supply list, and what to expect on day one.
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5 border-l-2 mb-8" style={{ borderColor: C.mint, background: C.card }}>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: C.mint }} />
                  <div>
                    <div className="text-[10px] font-black tracking-[0.28em] uppercase mb-1" style={{ color: C.mint }}>Where we meet</div>
                    <div className="text-sm md:text-base font-black">{LOCATION_ADDR}</div>
                    <div className="text-xs mt-1" style={{ color: C.dim }}>
                      Safe, tucked-away private corner — off the main street.
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-xs" style={{ color: C.dim }}>
                A receipt from Stripe and a confirmation from us have been emailed to
                {' '}<strong style={{ color: '#fff' }}>{state.data?.parent_email}</strong>.
                Reply to that email or text Margaret at any time.
              </div>

              <div className="mt-10">
                <Link
                  to="/prep-pod"
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em] border-2"
                  style={{ borderColor: C.subtle, color: '#fff' }}
                  data-testid="prep-pod-success-back"
                >
                  Back to The Prep Pod
                </Link>
              </div>
            </div>
          )}

          {!state.loading && !state.paid && (
            <div data-testid="prep-pod-success-unpaid">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ background: '#3F1F22' }}>
                <XCircle className="w-8 h-8" style={{ color: '#E63946' }} />
              </div>
              <h1 className="uppercase leading-[0.95] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
                Payment <span style={{ color: '#E63946' }}>Not Confirmed.</span>
              </h1>
              <p className="text-base mb-6" style={{ color: C.dim, lineHeight: 1.6 }}>
                {state.error || 'Stripe hasn\'t marked this session as paid yet. If your card was charged, refresh in a minute — payment settles fast. Otherwise, try again below or email Margaret directly.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/prep-pod#pricing"
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em]"
                  style={{ background: C.volt, color: C.ink }}
                  data-testid="prep-pod-success-retry"
                >
                  Try Again
                </Link>
                <a
                  href="mailto:Margaret@prsvr.com"
                  className="inline-flex items-center gap-2 py-3 px-5 text-xs font-black uppercase tracking-[0.22em] border-2"
                  style={{ borderColor: C.subtle, color: '#fff' }}
                  data-testid="prep-pod-success-email-margaret"
                >
                  Email Margaret
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
